import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from "framer-motion"
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, Sparkles, Zap, Brain, Shield, Lock, Mail, User, Eye, EyeOff } from 'lucide-react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import Button from '../components/buttons/Button'
import InputField from '../components/inputFields/InputField'
import talkidaLogo from '../assets/icons/logo.png'
import { toast } from 'react-toastify'

// Simple Lock SVG icon component
const LockIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
    />
  </svg>
)

// Yup validation schemas
const signinValidationSchema = Yup.object({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required')
})

const signupValidationSchema = Yup.object({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Please confirm your password')
})

const Signin = () => {
  const { login, register, isLoading } = useAuth();
  const navigate = useNavigate()
  const [isAnimating, setIsAnimating] = useState(false)
  const [focusedField, setFocusedField] = useState(null)
  const [generalError, setGeneralError] = useState('')
  const [hidePassword, setHidePassword] = useState(true)
  const [hideConfirmPassword, setHideConfirmPassword] = useState(true)
  const [showLoginForm, setShowLoginForm] = useState(false)
  const [isSignupMode, setIsSignupMode] = useState(false)


  const handleInputFocus = (fieldName) => {
    setFocusedField(fieldName)
  }

  const handleInputBlur = () => {
    setFocusedField(null)
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimating(true)
      setShowLoginForm(true)
    }, 300)
    return () => clearTimeout(timer)
  }, [])

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      setGeneralError('')

      if (isSignupMode) {
        // Handle signup
        const result = await register(values.email, values.password);
        if (result.success) {
          toast.success("Account created successfully! Please Login to continue...", {
            position: "top-center",
            autoClose: 1500,
          })
          toggleMode();
        } else {
          setGeneralError(result.error || 'Registration failed. Please try again.');
          toast.error(result.error || 'Registration failed. Please try again.');
        }
      } else {
        // Handle signin
        const result = await login(values.email, values.password);
        if (result.success) {
          toast.success("Login successful! Redirecting to dashboard...", {
            position: "top-center",
            autoClose: 1500,
          })
          navigate('/dashboard');
        } else {
          setGeneralError(result.error || 'Login failed. Please try again.');
          toast.error(result.error || 'Login failed. Please try again.');
        }
      }
    } catch (error) {
      setGeneralError('An unexpected error occurred. Please try again.');
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const togglePassword = () => {
    setHidePassword(!hidePassword);
  }

  const toggleConfirmPassword = () => {
    setHideConfirmPassword(!hideConfirmPassword);
  }

  const toggleMode = () => {
    setIsSignupMode(!isSignupMode);
    setGeneralError('');
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        when: "beforeChildren",
        staggerChildren: 0.2
      }
    }
  }

  const formVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 100
      }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  }

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.3,
        duration: 0.6,
        ease: "easeOut"
      }
    }
  }

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        delay: 0.6,
        duration: 0.4,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.03,
      boxShadow: "0px 10px 20px rgba(59, 130, 246, 0.3)",
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    },
    tap: {
      scale: 0.97
    }
  }

  const BubblesBackground = () => {
    const bubbles = Array.from({ length: 10 })

    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {bubbles.map((_, index) => (
          <motion.div
            key={index}
            className="absolute rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 backdrop-blur-sm"
            style={{
              width: `${Math.random() * 200 + 50}px`,
              height: `${Math.random() * 200 + 50}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            initial={{ opacity: 0.1, scale: 0.8 }}
            animate={{
              y: [0, Math.random() * -100 - 50],
              x: [0, (Math.random() - 0.5) * 50],
              opacity: [0.1, 0.3, 0.1],
              scale: [0.8, 1.2, 0.9],
              rotate: [0, Math.random() * 360],
            }}
            transition={{
              duration: Math.random() * 20 + 15,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        ))}
      </div>
    )
  }

  const features = [
    {
      icon: Brain,
      title: 'AI-Powered Chatbots',
      description: 'Create intelligent chatbots that understand and respond naturally',
      delay: 0
    },
    {
      icon: Zap,
      title: 'Multi-Channel Support',
      description: 'Deploy across websites, social media, and messaging platforms',
      delay: 200
    },
    {
      icon: LockIcon,
      title: 'Enterprise Security',
      description: 'Bank-level security with end-to-end encryption',
      delay: 400
    }
  ]

  return (
    <>
      <Helmet>
        <title>Sign In | Talkida - AI Chatbot Platform</title>
        <meta name="description" content="Sign in to your Talkida account to manage your AI chatbots and access your dashboard." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden relative">
        <BubblesBackground />

        {/* Security badge */}
        <motion.div
          className="absolute top-4 left-4 z-50 flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full shadow-xl border border-white/20"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Shield className="text-green-400 text-lg" />
          <span className="text-sm font-medium text-white">Secure Login</span>
        </motion.div>

        <div className="flex min-h-screen">
          {/* Left Panel - Content with Glassmorphic Design */}
          <motion.div
            className="hidden lg:flex lg:w-1/2 relative z-10 overflow-hidden"
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            {/* Glassmorphic gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 via-pink-500/15 to-blue-500/20 backdrop-blur-sm" />

            {/* Glassmorphic geometric patterns */}
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-20 right-20 w-32 h-32 border border-white/30 rounded-full backdrop-blur-sm" />
              <div className="absolute bottom-32 left-16 w-24 h-24 border border-white/30 rounded-lg rotate-45 backdrop-blur-sm" />
              <div className="absolute top-1/2 right-1/4 w-16 h-16 border border-white/30 rounded-full backdrop-blur-sm" />
            </div>

            <motion.div
              className="relative w-full flex flex-col items-center justify-center text-center p-12"
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              {/* Glassmorphic floating elements */}
              <div className="absolute w-56 h-56 rounded-full bg-gradient-to-r from-purple-400/20 to-pink-400/30 blur-3xl -top-16 -left-16 animate-pulse backdrop-blur-sm" />
              <div className="absolute w-72 h-72 rounded-full bg-gradient-to-r from-blue-400/25 to-indigo-400/25 blur-3xl -bottom-24 -right-24 animate-pulse backdrop-blur-sm" style={{ animationDuration: '8s' }} />

              {/* Content */}
              <motion.div
                className="relative z-10 max-w-md"
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
              >
                <div className="w-full max-w-sm mx-auto mb-8">
                  <div className="flex items-center justify-center space-x-4 mb-6">
                    <div className="p-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
                      <Brain className="w-12 h-12 text-white" />
                    </div>
                    <div className="p-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
                      <Zap className="w-12 h-12 text-white" />
                    </div>
                    <div className="p-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
                      <Shield className="w-12 h-12 text-white" />
                    </div>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-white mb-4">
                  Build the Future of Customer Service
                </h2>
                <p className="text-white/80 leading-relaxed mb-6">
                  Create intelligent AI chatbots that understand your customers and provide
                  instant, accurate responses 24/7. Transform your customer experience with
                  the power of artificial intelligence.
                </p>

                {/* Feature highlights */}
                <div className="space-y-3 text-left">
                  {features.map((feature, index) => (
                    <motion.div
                      key={feature.title}
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                    >
                      <div className="w-2 h-2 bg-white/60 rounded-full" />
                      <span className="text-sm text-white/80">{feature.title}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Glassmorphic light beams */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute w-full h-80 bg-white/10 rotate-12 transform -translate-x-1/2 -translate-y-full animate-beam backdrop-blur-sm" />
                <div className="absolute w-full h-80 bg-white/5 -rotate-12 transform translate-x-1/2 -translate-y-full animate-beam backdrop-blur-sm" style={{ animationDelay: '3s' }} />
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            className="w-full lg:w-1/2 flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8 relative z-10 min-h-screen lg:min-h-0"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="w-full max-w-md relative">
              {/* Glassmorphic decorative elements */}
              <div className="absolute -top-20 -left-20 w-40 h-40 bg-gradient-to-r from-purple-500/40 to-pink-500/30 rounded-full blur-3xl animate-pulse backdrop-blur-sm"></div>
              <div className="absolute -bottom-20 -right-20 w-56 h-56 bg-gradient-to-l from-blue-500/40 to-indigo-500/30 rounded-full blur-3xl animate-pulse backdrop-blur-sm" style={{ animationDelay: '1s' }}></div>

              {/* Logo with enhanced animation */}
              <motion.div className="text-center mb-6 sm:mb-8">
                <motion.img
                  src={talkidaLogo}
                  alt="Talkida Smart Chat Solutions"
                  className="h-16 sm:h-20 mx-auto drop-shadow-lg mb-3 sm:mb-4"
                  initial={{ y: -50, opacity: 0, scale: 0.8 }}
                  animate={{ y: 0, opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />

                <motion.div variants={titleVariants}>
                  <h1 className="font-bold text-2xl sm:text-3xl lg:text-4xl text-center bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-200 to-pink-200 mb-2 leading-tight">
                    {isSignupMode ? 'Create Account' : 'Welcome Back'}
                  </h1>
                  <p className="text-white/80 text-sm sm:text-base font-medium px-2">
                    {isSignupMode
                      ? 'Join Talkida and start building AI chatbots'
                      : 'Sign in to access your AI chatbot dashboard'
                    }
                  </p>
                </motion.div>
              </motion.div>

              <AnimatePresence>
                {showLoginForm && (
                  <motion.div
                    variants={formVariants}
                    initial="hidden"
                    animate="visible"
                    className="bg-white/10 backdrop-blur-xl p-6 sm:p-8 rounded-3xl shadow-2xl border border-white/20 relative overflow-hidden"
                  >
                    {/* Form header with security indicator */}
                    <div className="text-center mb-6">
                      <div className="flex items-center justify-center gap-2 mb-3">
                        <Lock className="text-white/80 text-lg" />
                        <span className="text-sm text-white/90 font-medium">
                          {isSignupMode ? 'Secure Registration' : 'Secure Sign In'}
                        </span>
                      </div>
                    </div>

                    <Formik
                      initialValues={{
                        name: '',
                        email: '',
                        password: '',
                        confirmPassword: '',
                        rememberMe: false,
                      }}
                      validationSchema={isSignupMode ? signupValidationSchema : signinValidationSchema}
                      onSubmit={handleSubmit}
                    >
                      {({ isSubmitting, values, errors, touched }) => (
                        <Form className="space-y-6">
                          {/* Email Field with Icon */}
                          <motion.div variants={itemVariants}>
                            <div className="relative">
                              <InputField
                                label="Email Address"
                                name="email"
                                type="email"
                                placeholder="Enter your email"
                                required
                                className="pl-12 py-4 rounded-2xl bg-white/10 backdrop-blur-sm w-full border-white/30 focus:ring-2 focus:ring-white/30 focus:border-white/50 transition-all duration-300 text-base text-white placeholder-white/60"
                              />
                              <div className="absolute left-4 top-[calc(1.5rem+1.25rem)] flex items-center pointer-events-none z-10">
                                <Mail className="h-5 w-5 text-white/70" />
                              </div>
                            </div>
                            {/* Email validation indicator */}
                            {values.email && !errors.email && touched.email && (
                              <motion.div
                                className="flex items-center gap-1 mt-2 text-green-400"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                              >
                                <Shield className="h-4 w-4" />
                                <span className="text-sm">Valid email format</span>
                              </motion.div>
                            )}
                          </motion.div>

                          {/* Password Field with Strength Indicator */}
                          <motion.div variants={itemVariants}>
                            <div className="relative">
                              <InputField
                                label="Password"
                                name="password"
                                type={hidePassword ? "password" : "text"}
                                placeholder="Enter your password"
                                required
                                isPassword={true}
                                showPassword={!hidePassword}
                                onPasswordToggle={togglePassword}
                                className="pl-12 py-4 rounded-2xl w-full bg-white/10 backdrop-blur-sm border-white/30 focus:ring-2 focus:ring-white/30 focus:border-white/50 transition-all duration-300 text-base text-white placeholder-white/60"
                              />
                              <div className="absolute left-4 top-[calc(1.5rem+1.25rem)] flex items-center pointer-events-none z-10">
                                <Lock className="h-5 w-5 text-white/70" />
                              </div>
                            </div>
                            {/* Password strength indicator */}
                            {values.password && (
                              <motion.div
                                className="mt-2"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                              >
                                <div className="flex gap-1 mb-1">
                                  {[1, 2, 3, 4].map((level) => (
                                    <div
                                      key={level}
                                      className={`h-1 flex-1 rounded-full transition-colors duration-300 ${values.password.length >= level * 2
                                        ? values.password.length >= 8
                                          ? 'bg-green-500'
                                          : values.password.length >= 6
                                            ? 'bg-yellow-500'
                                            : 'bg-red-500'
                                        : 'bg-gray-200'
                                        }`}
                                    />
                                  ))}
                                </div>
                                <span className={`text-xs ${values.password.length >= 8
                                  ? 'text-green-400'
                                  : values.password.length >= 6
                                    ? 'text-yellow-400'
                                    : 'text-red-400'
                                  }`}>
                                  {values.password.length >= 8
                                    ? 'Strong password'
                                    : values.password.length >= 6
                                      ? 'Good password'
                                      : 'Password too short'
                                  }
                                </span>
                              </motion.div>
                            )}
                          </motion.div>

                          {/* Confirm Password Field for Signup */}
                          {isSignupMode && (
                            <motion.div variants={itemVariants}>
                              <div className="relative">
                                <InputField
                                  label="Confirm Password"
                                  name="confirmPassword"
                                  type={hideConfirmPassword ? "password" : "text"}
                                  placeholder="Confirm your password"
                                  required
                                  isPassword={true}
                                  showPassword={!hideConfirmPassword}
                                  onPasswordToggle={toggleConfirmPassword}
                                  className="pl-12 py-4 rounded-2xl w-full bg-white/10 backdrop-blur-sm border-white/30 focus:ring-2 focus:ring-white/30 focus:border-white/50 transition-all duration-300 text-base text-white placeholder-white/60"
                                />
                                <div className="absolute left-4 top-[calc(1.5rem+1.25rem)] flex items-center pointer-events-none z-10">
                                  <Lock className="h-5 w-5 text-white/70" />
                                </div>
                              </div>
                              {/* Password match indicator */}
                              {values.confirmPassword && (
                                <motion.div
                                  className="mt-2"
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                >
                                  <div className={`flex items-center gap-1 ${values.password === values.confirmPassword ? 'text-green-400' : 'text-red-400'}`}>
                                    <Shield className="h-4 w-4" />
                                    <span className="text-sm">
                                      {values.password === values.confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                                    </span>
                                  </div>
                                </motion.div>
                              )}
                            </motion.div>
                          )}

                          {/* Submit Button */}
                          <motion.div
                            variants={buttonVariants}
                            whileHover="hover"
                            whileTap="tap"
                          >
                            <Button
                              type="submit"
                              className="w-full bg-gradient-to-r from-white/20 to-white/30 hover:from-white/30 hover:to-white/40 text-white font-semibold py-4 px-6 rounded-2xl shadow-xl hover:shadow-2xl backdrop-blur-sm border border-white/30 transition-all duration-300 text-base disabled:opacity-70 disabled:cursor-not-allowed"
                              disabled={isSubmitting || isLoading}
                              icon={ArrowRight}
                              iconPosition="right"
                            >
                              {isSubmitting || isLoading
                                ? (isSignupMode ? 'Creating Account...' : 'Signing In...')
                                : (isSignupMode ? 'Create Account' : 'Sign In to Dashboard')
                              }
                            </Button>
                          </motion.div>

                          {/* Mode Toggle */}
                          <motion.div
                            className="text-center pt-4"
                            variants={itemVariants}
                          >
                            <button
                              type="button"
                              onClick={toggleMode}
                              className="text-sm text-white/80 hover:text-white transition-colors duration-200 underline underline-offset-2 hover:underline-offset-4"
                            >
                              {isSignupMode
                                ? "Already have an account? Sign in"
                                : "Don't have an account? Sign up"
                              }
                            </button>
                          </motion.div>

                          {/* Security notice */}
                          <motion.div
                            className="text-center pt-2"
                            variants={itemVariants}
                          >
                            <p className="text-xs text-white/70">
                              Protected by enterprise-grade security
                            </p>
                          </motion.div>
                        </Form>
                      )}
                    </Formik>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Footer */}
              <motion.div
                className="text-center mt-6 sm:mt-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 0.5 }}
              >
                <p className="text-xs text-white/60 mt-1">
                  Powered by Talkida Technologies
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Glassmorphic global styles */}
        <style>{`
        @keyframes beam {
          0% { transform: translateY(-100%) rotate(12deg); }
          50% { transform: translateY(50vh) rotate(12deg); }
          100% { transform: translateY(150vh) rotate(12deg); }
        }
        .animate-beam {
          animation: beam 12s infinite linear;
          animation-delay: 2s;
        }
        
        /* Glassmorphic scrollbar */
        ::-webkit-scrollbar {
          width: 6px;
        }
        ::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 3px;
          backdrop-filter: blur(10px);
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }
        
        /* Enhanced glassmorphic focus transitions */
        input:focus, button:focus {
          transition: all 0.3s ease-in-out;
          backdrop-filter: blur(20px);
        }
        
        /* Glassmorphic mobile optimization */
        @media (max-width: 640px) {
          .bg-white\\/10 {
            background-color: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(20px);
          }
        }
        
        /* Prevent zoom on input focus for iOS */
        @media screen and (max-width: 767px) {
          input[type="email"], input[type="password"], input[type="text"] {
            font-size: 16px;
          }
        }
        
        /* Glassmorphic glow effects */
        .glassmorphic-glow {
          box-shadow: 
            0 8px 32px rgba(255, 255, 255, 0.1),
            0 0 0 1px rgba(255, 255, 255, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.3);
        }
        
        /* Enhanced backdrop blur */
        .backdrop-blur-glass {
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
        }
      `}</style>
      </div>
    </>
  )
}

export default Signin
